import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useSelector, shallowEqual } from 'umi';
import { Col, Row } from "tea-component";
import { useSelectArrObj, useSelectorMap } from '@/pages/dashboard/utils/customHook';
import CountryRegionRowItem from '@/pages/dashboard/components/CountryRegionRowItem';
import BreakDownHeader from '@/pages/dashboard/components/BreakDownHeader';
import { useNightingaleBreakDown } from './_hooks';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { useData } from './useData';
import { commonValueFormatter, formatExcelString, formatExeclCommon, formatExeclNormalPercent, formatExeclNum, formatExeclSharePercent, getSystemColor, handleDownload } from '@/pages/dashboard/utils/utils';
import moment, { locale } from 'moment';
import { COLOR_ARR } from '@/pages/dashboard/_config/constant';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { getSystemFlag } from '../../../_config/system';
import { dateFormatStr } from '@/pages/dashboard/_config/dateFormatConfig';
import { yAxisFormat } from '@/pages/dashboard/components/Chart/_config/utils';
import getSystemLegend from '@/pages/dashboard/components/CountryRegionRowItem/CountryTabChartsItem/utils';

const NightingaleBreakDown = (props: any) => {
  const { config } = props;
  const { metricsInit, metricsList, dimensionInit, api } = config || {};
  const contextObj = useContext(FilterContext);
  const { filterVal } = contextObj || {};
  const { gameCode, selector } = useSelector((state: any) => ({
    gameCode: state.dashboard?.currentGame?.game_code,
    selector: state?.dashboardSetting?.selector ?? {},
  }), shallowEqual);
  const { getFilterItemText } = useSelectorMap();
  const accumatedOptions = useSelectArrObj(filterVal, false, true);
  const [metricsName, setMetricsName] = useState(metricsInit);
  const [accumated, setAccumated] = useState(dimensionInit || 'system');
  // 内部状态放组件中，方便后续定位查看，其他信息放在hooks：useNightingaleBreakDown中
  const { options, title, prefix, titleContent, isSingle, comChartConfig, metricsConfigObj, chartTooltipsName,
    getParams, tableColumns } = useNightingaleBreakDown({ data, metricsInit, accumated, metricsName, filterVal, gameCode, metricsList });
  const { chartCustomConfig, initChartIndex, type, decimal, percentDecimals } = metricsConfigObj || {};
  const { chartLineBarDataFrom, isCustomBarChart } = chartCustomConfig || {};
  const { data, getData, isLoading, isError } = useData({ metricsName, accumated, api, getParams, chartLineBarDataFrom });

  const customBarOptions = useMemo(() => {
    if (!isCustomBarChart) {
      return null;
    }
    let chartData = data?.customChartData || [];
    chartData = chartData?.length >= 11 ? chartData : chartData?.filter(v => !(v?.name === 'Others' && v?.value === ''));
    const legendDataList = chartData?.map((item) => getFilterItemText(accumated, item?.name, item?.full_name)) || [];
    const accumatedDataOri = data?.customChartData.map((item) => item?.full_name);
    const isSystem = (accumated === 'os' || accumated === 'system');
    const barSeries = legendDataList?.map((item, index) => ({
      data: chartData?.map((dataItem, dataIndex) => {
        if (dataIndex === index) {
          return dataItem?.value;
        } 
        return 0;
          
      }),
      color: COLOR_ARR,
      cursor: "pointer",
      type: 'bar',
      stack: 'overviewBar',
      name: item,
      // ...(index !== 0 ?{barWidth:0} : {})
    }));
    const barFormatter = (param: any[]) => {
      const [starTime, endTime] = filterVal?.rangeTime || [];
      const pieTitle = `${moment(starTime).format(dateFormatStr)  }~${  moment(endTime).format(dateFormatStr)}`;
      const dataArr = param?.filter(item=>item?.value);
      if(!dataArr?.[0]?.value){
        return;
      }
      return getEchartTooltipText({
        title: '',
        // metricsName: chartTooltipsName,
        metricsName: pieTitle,
        ratioName: '',
        hasRatio: false,
        data: [dataArr[0]]?.map((item: any) => {
          const {value, color } = item || {};
          return {
            name: item?.name,
            icon: accumated === 'os' || accumated === 'system' ? getSystemFlag(item?.data?.accumatedName) : '',
            metricsNum: commonValueFormatter(type, value, decimal),
            // @ts-ignore
            // ratioNum: item?.data?.shareOfAll,
            color,
            customColor: isSystem,
          };
        }),
        isSort: true,
      });
    };
    // 需求很急，热更上线，先临时处理逻辑，后续优化
    const options = {
      customLegendData: legendDataList,
      customXAxisData: legendDataList,
      customBarSeries: barSeries,
      customBarFormatter: barFormatter,
      customBarSetOther: (opt: any, isShowEyes: boolean) => {
        // 注意此操作只能放到这里，如果放在之前barSeries中，则会导致数据标签失效
        opt.series = opt.series?.map((item: any, index: number) => ({
          ...item,
          data: chartData?.map((dataItem, dataIndex) => {
            const resut = {
              ...opt.series[dataIndex]?.data?.[dataIndex],
              value: index === dataIndex ? dataItem?.value : 0,
              name: legendDataList?.[dataIndex],
              accumatedName: accumatedDataOri?.[dataIndex],
              // shareOfAll: commonValueFormatter('percent', item?.share_of_all, 1),
              type: "bar",
              cursor: "pointer",
              stack: 'overviewBar',
              barCategoryGap: "50%",
              itemStyle: {
                color: isSystem ? getSystemColor(accumatedDataOri?.[dataIndex]) : COLOR_ARR[dataIndex],
              },
              label: {
                position: 'top',
                show: metricsConfigObj?.comShowConfig?.isHaveEyes && index === dataIndex && isShowEyes,
              },
            };
            return resut;
          }),
          label: {
            normal: {
              formatter: (params: { value: string | number; }) => {
                const result = commonValueFormatter(type, params?.value, decimal);
                return result;
              },
            }
          }
        }));

        if (type === 'percent') {
          opt.yAxis.axisLabel.formatter = (value: number) => yAxisFormat(value, true, 2, true);
        }
        getSystemLegend(opt, selector);
        if (opt?.legend?.data) {
          opt.legend.data = opt.legend.data.map(((legendItem: any, index: number) => ({
            ...legendItem,
            itemStyle: {
              color: isSystem ? getSystemColor(accumatedDataOri?.[index]) : COLOR_ARR[index],
            }
          })));
        }
        return opt;
      },
    };

    return options;
  }, [isCustomBarChart, data]);

  const downloadFunc = useCallback(() => {
    const hasOthersValue = data?.tableData?.find((item: { name: string; }) => item?.name === 'Others')?.value !== '';
    const downloadDatas: any = [];
    data?.tableData?.forEach((item: any) => {
      if (!hasOthersValue && item?.full_name === 'Others') return;
      const values = item?.value;
      downloadDatas.push({
        name: getFilterItemText(accumated,item?.name, item?.full_name, true),
        [metricsName]: type === 'percent' ? formatExeclNormalPercent(values) : type === 'int' ? formatExeclNum(values) : formatExeclCommon(values),
        share_of_all: formatExeclSharePercent(item?.share_of_all),
      });
    });
    const tableConfig = {
      option: tableColumns,
      downloadData: downloadDatas
    };
    handleDownload(tableConfig, titleContent?.name, false);
  }, [data, locale, metricsName, tableColumns, accumated]);

  const onMetricsChange = (val: string) => {
    setMetricsName(val);
    getData(getParams(accumated, val));
  };

  const onAccumatedChange = (val: string) => {
    setAccumated(val);
    getData(getParams(val, metricsName));
  };

  return (
    <>
      <BreakDownHeader
        defaultValue={metricsInit}
        filterVal={filterVal}
        onChange={onMetricsChange}
        AccumatedConfig={{
          onAccumatedChange,
          accumatedOptions,
          accumatedDefault: 'system',
          isHaveAccumulatedSelector: true
        }}
        options={options}
      ></BreakDownHeader>
      <Row gap={24}>
        <Col span={24}>
          {
            <CountryRegionRowItem
              prefix={prefix}
              titleName={title}
              barChartType={metricsName === 'asp' ? "multicolumnChart" : 'stackChart'}
              titleContent={titleContent}
              regionDataType={accumated === 'system' ? 'os' : accumated}
              comConfig={metricsConfigObj?.comShowConfig || comChartConfig}
              timeRange={filterVal?.rangeTime}
              initActiveIndex={initChartIndex ? initChartIndex : 0}
              loading={isLoading}
              isSingle={isSingle}
              isError={isError}
              data={data}
              isUserQuota={type === 'int'}
              timeType={filterVal?.timeType}
              chartTooltipsConfig={{
                tooltipsName: chartTooltipsName,
                tooltipsType: type,
                tooltipsDecimal: decimal,
                tooltipsPercentDecimals: percentDecimals,
              }}
              customChartOptions={{
                customBarOptions
              }}
              tableColumns={tableColumns}
              downloadFunc={downloadFunc}
              gameCode={gameCode}
              // titleAndType={titleAndType}
              showConfig={metricsConfigObj?.showConfig}
              morePie
            />
          }

        </Col>
      </Row>
    </>
  );
};

export default NightingaleBreakDown;